import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import { getBpm } from "../../utils/bpmUtil";
import {
  S3CredentialsContext,
  S3CredentialsContextInterface,
} from "../../contexts";
import { AuthContext, AuthContextInterface } from "../../contexts";
import {
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import {
  FileState,
  Folder,
  PostFolderResponse,
  ProjectFile,
  TrackProcessorInfo,
} from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { postTrackBatch } from "../../services/trackService";
import { postFolder } from "../../services/folderService";
import { FileStateReverseMap } from "../SingleProject/types";
import { postProjectFiles } from "../../services/projectService";
import { addItemsModalStyles, batchTrackAddModalStyles } from "./styles";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import Mixpanel from "../../utils/mixpanel";
import CustomButton from "../CustomButton/CustomButton";
import { FolderUploadProps } from "./types";
import { toast } from "react-toastify";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const FolderUpload: React.FC<FolderUploadProps> = ({
  setIsUploading,
  handleCloseBatchTrackAddModal,
  setIsUploadProcessing,
  setFolderUploadInfo,
  handleOpenMessageModal,
}) => {
  const { getValidAccessToken } = useGetValidAccessToken();
  const [filesFromFolder, setFilesFromFolder] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [generalFilesFromFolder, setGeneralFilesFromFolder] = useState<File[]>(
    []
  );
  const [folderToUpload, setFolderToUpload] = useState<string>("");
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise, isEnterpriseAccount } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);

  const handleProgress = (pNum: number, idx: number, filesList: any[]) => {
    if (pNum === 100) {
      setFolderUploadInfo((prevInfo) => {
        if (!prevInfo) return prevInfo;
        return {
          ...prevInfo,
          uploadedFiles: prevInfo.uploadedFiles + 1,
        };
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const audioFiles: File[] = [];
    const generalFiles: File[] = [];
    Array.from(files).forEach((file) => {
      if (file.type.startsWith("audio/")) {
        audioFiles.push(file);
      } else {
        generalFiles.push(file);
      }
    });

    if (audioFiles.length === 0 && generalFiles.length === 0) {
      return;
    }

    setFilesFromFolder(audioFiles);
    setGeneralFilesFromFolder(generalFiles);

    const firstFile = files[0];
    if (firstFile && "webkitRelativePath" in firstFile) {
      const folderName = firstFile.webkitRelativePath.split("/")[0];
      setFolderToUpload(folderName);
    }
  };

  const handleClearFolder = () => {
    setFilesFromFolder([]);
    setFolderToUpload("");
  };
  const handleClose = () => {
    setFolderToUpload("");
    handleCloseBatchTrackAddModal();
  };

  const handleFolderUpload = async () => {
    if (folderToUpload === "") {
      toast.warn("Please select folder to upload");
      return;
    }
    setIsLoading(true);
    setIsUploading(true);
    setIsUploadProcessing(true);
    handleCloseBatchTrackAddModal();

    const fileArray = Array.from(filesFromFolder);
    const generalFilesArray = Array.from(generalFilesFromFolder);
    const allFiles = [...fileArray, ...generalFilesArray];

    const firstFile = allFiles[0];
    const firstFilePathParts = firstFile.webkitRelativePath.split("/");
    const rootFolderName = firstFilePathParts[0];

    setFolderUploadInfo({
      name: rootFolderName,
      totalFiles: allFiles.length,
      uploadedFiles: 0,
    });

    const folderIdMap = new Map<string, string>();
    const uniqueFolders = new Set<string>();

    allFiles.forEach((file) => {
      const pathParts = (file as any).webkitRelativePath.split("/");
      pathParts.pop();
      let currentPath = "";
      for (const part of pathParts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        uniqueFolders.add(currentPath);
      }
    });

    const sortedFolders = Array.from(uniqueFolders).sort(
      (a, b) => a.split("/").length - b.split("/").length
    );

    for (const folderPath of sortedFolders) {
      const folderName = folderPath.split("/").pop() ?? "";
      const parentFolderPath =
        folderPath.split("/").slice(0, -1).join("/") || null;

      const folder = new Folder();
      folder.setName(folderName);
      folder.setFolderState(FileState.FILE_STATE_ACTIVE);
      folder.setOwnerId(userId);

      if (enterprise?.id) {
        folder.setEnterpriseId(enterprise.id);
      }

      if (parentFolderPath && folderIdMap.has(parentFolderPath)) {
        folder.setParentFolderId(folderIdMap.get(parentFolderPath)!);
      }

      const token = await getValidAccessToken();
      const res = await postFolder(token, folder);

      if (res instanceof PostFolderResponse) {
        const folderId = res.getFolder()?.getId();
        if (folderId) {
          folderIdMap.set(folderPath, folderId);
        }
      }
    }

    if (fileArray.length > 0) {
      const tracksProcessorInfoList: TrackProcessorInfo[] = [];
      const uploadPromises = [];

      for (const file of fileArray) {
        const fileFolderPath = (file as any).webkitRelativePath
          .split("/")
          .slice(0, -1)
          .join("/");
        const parentFolderId = folderIdMap.get(fileFolderPath) ?? null;

        const uploadPromise = awaitableUploadToS3(
          userId,
          file,
          `song`,
          file.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) =>
            handleProgress(progress, fileArray.indexOf(file), fileArray)
        ).then(async (songKey) => {
          const songUrl = `${ASSETS_URL}/${songKey}`;
          const newTrack = new TrackProcessorInfo();
          newTrack.setFilename(file.name);
          newTrack.setTrackUrl(songUrl);
          newTrack.setParentFolderId(parentFolderId ?? "");

          let bpm = 0;
          try {
            bpm = await getBpm(file);
          } catch (error) {
            console.log("BPM detection Error Batch Upload", error);
            bpm = 0;
          }

          newTrack.setBpm(bpm);
          tracksProcessorInfoList.push(newTrack);
        });

        uploadPromises.push(uploadPromise);
      }

      await Promise.all(uploadPromises);

      if (tracksProcessorInfoList.length > 0) {
        const token = await getValidAccessToken();
        const postBatchTrackResponse = await postTrackBatch(
          tracksProcessorInfoList,
          "",
          "",
          [],
          [],
          userId,
          "",
          "",
          "",
          token,
          isEnterpriseAccount && enterprise?.id ? enterprise.id : undefined
        );

        if (postBatchTrackResponse instanceof RpcError) {
          throw postBatchTrackResponse;
        }
      }
    }

    const generalFilesUploadPromises = [];
    const uploadedFiles: ProjectFile[] = [];

    for (const file of generalFilesArray) {
      const fileFolderPath = (file as any).webkitRelativePath
        .split("/")
        .slice(0, -1)
        .join("/");
      const parentFolderId = folderIdMap.get(fileFolderPath) ?? null;

      const uploadPromise = awaitableUploadToS3(
        userId,
        file,
        `project-file`,
        file.type,
        awsAccessKeyId,
        awsSecretAccessKey,
        awsSessionToken,
        (progress) =>
          handleProgress(
            progress,
            generalFilesArray.indexOf(file),
            generalFilesArray
          )
      ).then(async (fileKey) => {
        const fileUrl = `${ASSETS_URL}/${fileKey}`;
        const newFile = new ProjectFile();
        newFile.setFileName(file.name);
        newFile.setFileUrl(fileUrl);
        newFile.setFileState(FileStateReverseMap[FileState.FILE_STATE_ACTIVE]);
        newFile.setFileSize(file.size);
        newFile.setOwnerId(userId);
        newFile.setProjectId("");

        if (enterprise?.id) {
          newFile.setEnterpriseId(enterprise.id);
        }

        newFile.setParentFolderId(parentFolderId ?? "");
        newFile.setType(file.type);
        uploadedFiles.push(newFile);
      });

      generalFilesUploadPromises.push(uploadPromise);
    }

    await Promise.all(generalFilesUploadPromises);

    if (generalFilesArray.length > 0) {
      const newToken = await getValidAccessToken();
      const res = await postProjectFiles(newToken, uploadedFiles);

      if (res instanceof RpcError) {
        throw res;
      }
    }

    Mixpanel.track("folder_upload_success", {
      audioFiles: fileArray.length,
      generalFiles: generalFilesArray.length,
    });

    setIsLoading(false);
    handleOpenMessageModal();
    setIsUploadProcessing(false);
  };

  return (
    <Box>
      {!folderToUpload && (
        <Box sx={batchTrackAddModalStyles.uploadButtonContainer}>
          <label htmlFor="folder_input">
            <input
              type="file"
              onChange={(e) => handleFileChange(e)}
              id="folder_input"
              multiple
              {...({
                webkitdirectory: "",
                directory: "",
              } as React.HTMLAttributes<HTMLInputElement>)}
              style={{ display: "none" }}
            />
            <Box sx={batchTrackAddModalStyles.uploadContainer}>
              <Typography>
                Note: Uploading ZIP files and empty folders is not supported.
              </Typography>
              <Button
                variant="contained"
                component="span"
                startIcon={<CloudUploadIcon />}
                sx={batchTrackAddModalStyles.uploadButton}
              >
                <Typography variant="body1" fontWeight="bold">
                  Select folder to upload
                </Typography>
              </Button>
            </Box>
          </label>
        </Box>
      )}
      {folderToUpload && (
        <Box sx={batchTrackAddModalStyles.folderNameContainer}>
          <Typography sx={{ minWidth: "90%" }}>{folderToUpload}</Typography>
          <IconButton onClick={() => handleClearFolder()}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      <DialogActions sx={addItemsModalStyles.buttonsContainer}>
        <CustomButton
          text="Upload Folder"
          onClick={handleFolderUpload}
          customStyles={{ width: "150px" }}
          loading={isLoading}
        />
        <CustomButton
          text="Cancel"
          onClick={handleClose}
          isSkipButton={true}
          customStyles={{ width: "150px" }}
        />
      </DialogActions>
    </Box>
  );
};

export default FolderUpload;
